import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AppState } from '../../../../../core/reducers';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { Category, CategoryService } from '../../../../../core/lists';
import { TransactionType } from '../../../compliance/enums';
import { Program, Standard } from '../../../compliance/models';
import { ProgramService, StandardService } from '../../../compliance/services';
import { Form } from '../../models';
import { FormSystem } from '../../enums';
import { FormService } from '../../services';
import { CreateFormOnServer, UpdateFormOnServer } from '../../actions';
import { CreateFormSchemaComponent } from '../create-form-schema/create-form-schema.component';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-create-form',
  templateUrl: './create-form.component.html',
  styleUrls: ['./create-form.component.scss'],
})
export class CreateFormComponent implements AfterViewInit, OnInit {
  form: FormGroup;
  mapped = false;
  prevStandardIds: number[];
  formSystems: FormSystem[] = Object.values(FormSystem);
  getGlobalFormAutocomplete = this.formService.findForms.bind(this.formService, { isGlobal: true });
  getTeamAutocomplete = this.categoryService.getAllCategories.bind(this.categoryService);
  getProgramAutocomplete = this.programService.searchProgram.bind(this.programService);
  getStandardAutocomplete = this.standardService.searchStandard.bind(this.standardService);
  @ViewChild('globalFormAutocomplete') globalFormAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('teamAutocomplete') teamAutocomplete: GeneralAutocompleteComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { form: Form; isSuperAdmin: boolean; useGlobal: boolean; viewMode: boolean },
    public stringUtils: StringUtilsService,
    private dialogRef: MatDialogRef<CreateFormComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private titleCasePipe: TitleCasePipe,
    private formService: FormService,
    private categoryService: CategoryService,
    private programService: ProgramService,
    private standardService: StandardService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: [this.data.form?.name, Validators.required],
      system: [this.data.form?.system],
      schema: [this.data.form?.schema, Validators.required],
      program: [null],
      standards: [null],
    });

    if (!this.data.isSuperAdmin) {
      this.form.get('system').setValidators(Validators.required);

      if (this.data.form?.id) {
        if (!this.data.viewMode)
          this.formService
            .findFormTemplateMaps(this.data.form.id)
            .pipe(take(1))
            .subscribe((res: any) => {
              if (res.data?.length) this.mapped = true;
            });

        this.standardService
          .getStandardListByTransaction(this.data.form.id, TransactionType.FORM)
          .pipe(take(1))
          .subscribe((standards) => {
            this.form.get('standards').patchValue(standards);
            this.prevStandardIds = standards?.map((standard) => standard.id) || [];
          });
      }
    }

    if (this.data.useGlobal || this.data.viewMode)
      Object.keys(this.form.controls).forEach((key) => this.form.get(key).disable());
  }

  ngAfterViewInit(): void {
    if (this.data.form?.id) {
      setTimeout(() => {
        this.teamAutocomplete?.setValue(this.data.form.teams);
        this.teamAutocomplete?.updateValueAndValidity();
      });
    }
  }

  getTitle(): string {
    return `FORMS_TEMPLATES_MODULE.FORM.${
      this.data.viewMode ? 'VIEW_TITLE' : this.data.form?.id ? 'UPDATE_TITLE' : 'CREATE_TITLE'
    }`;
  }

  formatFormOption(form: Form): string {
    return form?.name || '';
  }

  handleFormChange(form: Form): void {
    const formCtrls = this.form.controls;

    if (!form?.id) {
      formCtrls.name.setValue(null);
      formCtrls.schema.setValue(null);
      Object.keys(formCtrls).forEach((key) => this.form.get(key).disable());

      return;
    }

    formCtrls.name.setValue(form.name);
    formCtrls.schema.setValue(form.schema);
    Object.keys(formCtrls).forEach((key) => this.form.get(key).enable());
  }

  formatTeamOption(team: Category): string {
    return team?.name || '';
  }

  formatNewTeam(team: Category): Category {
    return team;
  }

  formatProgramOption(program: Program): string {
    return program?.name || '';
  }

  handleProgramChange(program: Program): void {
    this.form.get('program').patchValue(program);
    this.getStandardAutocomplete = this.standardService.searchStandard.bind(
      this.standardService,
      program?.id,
      false,
    );
  }

  formatStandardOption(standard: Standard): string {
    return standard
      ? `${standard.standard_reference}<small class="text-muted ml-4">${
          standard.program?.name || ''
        }</small>`
      : '';
  }

  formatNewStandard(standard: Standard): Standard {
    return standard;
  }

  handleStandardChange(standards: Standard[]): void {
    this.form.get('standards').patchValue(standards);
  }

  isInvalid(name: string): boolean {
    const field = this.form.get(name);

    return field.touched && field.invalid;
  }

  getErrorMessage(label: string): string {
    return `${label} field is required`;
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.globalFormAutocomplete?.updateValueAndValidity();
    this.form.markAllAsTouched();
    this.teamAutocomplete.updateValueAndValidity();

    if (
      this.globalFormAutocomplete?.isInvalid() ||
      this.form.invalid ||
      this.teamAutocomplete.isInvalid()
    )
      return;

    const formValue = this.form.value;
    const compliance: { ids: number[]; is_edited?: boolean } = {
      ids: formValue.standards?.map((standard) => standard.id) || [],
    };

    if (
      this.data.form?.id &&
      this.prevStandardIds?.sort().join('') !== compliance.ids.sort().join('')
    )
      compliance.is_edited = true;

    const form = new Form(
      this.data.form?.id,
      formValue.name,
      this.teamAutocomplete.value?.map((team) => team.id),
      formValue.system,
      formValue.schema,
      this.data.form?.version,
      compliance,
    );

    for (const [key, value] of Object.entries(form)) {
      if (typeof value === 'string') form[key] = value.trim();
    }

    this.data.form?.id
      ? this.store.dispatch(new UpdateFormOnServer({ form }))
      : this.store.dispatch(
          new CreateFormOnServer({ form, importedFormId: +this.globalFormAutocomplete?.value?.id }),
        );

    this.dialogRef.close({ form });
  }

  manageSchema(event: Event): void {
    event.stopPropagation();

    const form = { ...this.data.form };

    form.schema = this.form.get('schema').value;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = this.data.viewMode ? '60vw' : '95vw';
    dialogConfig.maxWidth = this.data.viewMode ? '60vw' : '95vw';
    dialogConfig.data = { form, viewMode: this.data.viewMode };

    const dialogRef = this.dialog.open(CreateFormSchemaComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((schema) => {
        this.form
          .get('schema')
          .patchValue(schema?.components?.length ? JSON.stringify(schema) : '');
        this.form.markAllAsTouched();
      });
  }
}
